import { useEffect, useMemo, useState } from 'react';
import { useSpecimens, useScanSpecimen, useReceiveSpecimen, useRejectSpecimen } from '../../services/httpServices/queries';
import type { LabSpecimen } from '../../services/httpServices/diagnosticService';
import { specimenCategoryNames } from '../../utils/specimen';
import { joinFullName } from '../../utils/patientName';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/Pagination';
import { SpecimenDetailsModal } from '../../components/ui/SpecimenDetailsModal';
import { toast } from 'sonner';
import { ScanBarcode, CheckCircle, XCircle, Loader2, AlertCircle, Search, FilterX, PackageCheck, User } from 'lucide-react';

const statusMeta: Record<string, { label: string; variant: 'warning' | 'secondary' | 'primary' | 'success' | 'danger' | 'default' }> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  COLLECTED: { label: 'Collected', variant: 'primary' },
  RECEIVED: { label: 'Received', variant: 'secondary' },
  PROCESSING: { label: 'Processing', variant: 'primary' },
  COMPLETED: { label: 'Completed', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', variant: 'danger' },
};

const specimenTests = (s: LabSpecimen) =>
  (s.items || []).map((i) => i.receiptItem?.testName).filter(Boolean).join(', ');

export function LabReceiving() {
  const { hasPermission } = usePermissions();
  const canReceive = hasPermission('diagnostics:specimen:receive');
  const canReject = hasPermission('diagnostics:specimen:reject');

  const [tubeBarcode, setTubeBarcode] = useState('');
  const [scanned, setScanned] = useState<LabSpecimen | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const limit = 20;
  const [rejectTarget, setRejectTarget] = useState<LabSpecimen | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const filters = useMemo(() => ({ status: 'COLLECTED', search: debouncedSearch || undefined }), [debouncedSearch]);
  const { data, isLoading, error } = useSpecimens(filters, page, limit);
  const queue = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const scanSpecimen = useScanSpecimen();
  const receiveSpecimen = useReceiveSpecimen();
  const rejectSpecimen = useRejectSpecimen();

  const refresh = (updated: LabSpecimen) => setScanned((prev) => (prev && prev.id === updated.id ? updated : prev));

  const handleScan = async () => {
    if (!tubeBarcode.trim()) return;
    try {
      const res = await scanSpecimen.mutateAsync(tubeBarcode.trim());
      setScanned(res.data);
      setTubeBarcode('');
    } catch (err: any) {
      toast.error(err?.message || 'Specimen not found for this barcode');
    }
  };

  const handleReceive = async (specimen: LabSpecimen) => {
    const res = await receiveSpecimen.mutateAsync(specimen.id);
    toast.success(`Received ${specimen.specimenBarcode}`);
    refresh(res.data);
  };

  const confirmReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    const res = await rejectSpecimen.mutateAsync({ id: rejectTarget.id, reason: rejectReason.trim() });
    toast.success(`Rejected ${rejectTarget.specimenBarcode}`);
    refresh(res.data);
    setRejectTarget(null);
    setRejectReason('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <PackageCheck className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-text">Lab Receiving</h1>
          <p className="text-xs text-text-muted">Scan collected tubes to accept them into the laboratory.</p>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <label className="block text-xs font-medium text-text-muted">Scan Tube Barcode</label>
        <div className="flex gap-2">
          <Input
            placeholder="Scan or type specimen barcode..."
            value={tubeBarcode}
            onChange={(e) => setTubeBarcode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
          />
          <Button onClick={handleScan} disabled={scanSpecimen.isPending || !tubeBarcode.trim()}>
            {scanSpecimen.isPending ? <Loader2 size={14} className="animate-spin" /> : <ScanBarcode size={14} />}
          </Button>
        </div>
      </div>

      {scanned && (
        <div className="bg-surface border border-primary/30 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <User size={16} className="text-primary" />
              <span className="text-sm font-semibold text-text">
                {joinFullName(scanned.patient || {})}
              </span>
              <Badge variant={statusMeta[scanned.status]?.variant || 'default'}>
                {statusMeta[scanned.status]?.label || scanned.status}
              </Badge>
            </div>
            <span className="text-[0.6875rem] font-mono text-text-muted">{scanned.specimenBarcode}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-text-muted">Container</p>
              <p className="text-text font-medium">
                {scanned.specimenContainerCode || 'N/A'}
                {scanned.containerColor ? ` · ${scanned.containerColor}` : ''}
              </p>
            </div>
            <div>
              <p className="text-text-muted">Category</p>
              <p className="text-text font-medium">{specimenCategoryNames(scanned) || 'N/A'}</p>
            </div>
            <div>
              <p className="text-text-muted">Collected</p>
              <p className="text-text font-medium">
                {scanned.collectedAt ? new Date(scanned.collectedAt).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-text-muted">Tests</p>
              <p className="text-text font-medium">{specimenTests(scanned) || 'N/A'}</p>
            </div>
          </div>
          {(canReceive || canReject) && (
            <div className="flex items-center gap-2 pt-2 border-t border-border/60">
              {scanned.status === 'COLLECTED' && canReceive && (
                <Button size="sm" onClick={() => handleReceive(scanned)} disabled={receiveSpecimen.isPending}>
                  <CheckCircle size={12} className="mr-1" /> Receive
                </Button>
              )}
              {scanned.status !== 'COMPLETED' && scanned.status !== 'CANCELLED' && scanned.status !== 'REJECTED' && canReject && (
                <Button variant="danger" size="sm" onClick={() => setRejectTarget(scanned)}>
                  <XCircle size={12} className="mr-1" /> Reject
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              placeholder="Search collected specimens..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {search && (
            <Button variant="ghost" size="sm" onClick={() => setSearch('')}>
              <FilterX size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        {error ? (
          <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
            <AlertCircle size={16} /> Failed to load the receiving queue
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : queue.length === 0 ? (
          <p className="text-sm text-text-muted py-8 text-center">No collected specimens awaiting receiving.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Specimen</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Container</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Tests</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {queue.map((s) => (
                  <tr key={s.id} className="hover:bg-background/40 transition-colors">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setDetailsId(s.id)}
                        className="text-xs font-mono text-primary hover:underline"
                        title="View specimen details"
                      >
                        {s.specimenBarcode}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-text">
                      {joinFullName(s.patient || {})}
                    </td>
                    <td className="px-3 py-2 text-sm text-text-muted">{specimenCategoryNames(s) || '—'}</td>
                    <td className="px-3 py-2 text-sm text-text-muted">
                      {s.specimenContainerCode || '—'}
                      {s.containerColor ? ` · ${s.containerColor}` : ''}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">{specimenTests(s)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {canReceive && (
                          <Button size="sm" onClick={() => handleReceive(s)} disabled={receiveSpecimen.isPending}>
                            <CheckCircle size={12} className="mr-1" /> Receive
                          </Button>
                        )}
                        {canReject && (
                          <Button variant="danger" size="sm" onClick={() => setRejectTarget(s)}>
                            <XCircle size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="specimens"
        />
      </div>

      <SpecimenDetailsModal specimenId={detailsId} onClose={() => setDetailsId(null)} />

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface border border-border rounded-xl shadow-lg w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-semibold text-text">Reject specimen</h2>
            <p className="text-sm text-text-muted">{rejectTarget.specimenBarcode} — resets linked orders for re-draw.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason (e.g. hemolysed, clotted, insufficient)"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmReject} disabled={!rejectReason.trim() || rejectSpecimen.isPending}>
                {rejectSpecimen.isPending && <Loader2 size={14} className="mr-1 animate-spin" />}
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}